import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import T from 'shared/atoms/Typography';
import Accordion from 'shared/molecules/Accordion';
import Input from 'shared/atoms/Input';

import theme from 'shared/themes/default';

import AnswerRadio from './AnswerRadio';

const HeaderStyled = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: ${theme.spacing.m1};
  background: transparent;
  gap: ${theme.spacing.s};
`;

const BodyStyled = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: ${theme.spacing.s} ${theme.spacing.m2};
  background: ${(props) =>
    props.theme === 'light'
      ? theme.palette.lightDistinct
      : theme.palette.darkWrapper};
`;

const BodyHeaderStyled = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: ${theme.spacing.s};
  gap: ${theme.spacing.s};
  align-items: flex-end;
`;

const QuestionAccordionHeader = ({
  disabled,
  expanded,
  index,
  onChangeQuestion,
  question,
  selectedLanguage,
  theme: themeLayout,
}) => {
  const sxTitle = {
    marginRight: theme.spacing.m1,
    alignSelf: 'baseline',
  };

  const mainColor =
    themeLayout === 'light' ? theme.palette.mainDark : theme.palette.lightPure;

  const questionTranslation = question.find(
    (q) => q.languageCode === selectedLanguage
  );
  const translatedQuestion = questionTranslation?.value || '';

  return (
    <HeaderStyled>
      <T sx={sxTitle} fontWeight="bold" color={mainColor}>
        {index + 1}
      </T>
      {expanded ? (
        <Input
          disabled={disabled}
          fullWidth
          maxRows={4}
          multiline
          onChange={(e) => onChangeQuestion(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          theme={themeLayout}
          value={translatedQuestion}
        />
      ) : (
        <T color={mainColor}>{translatedQuestion}</T>
      )}
    </HeaderStyled>
  );
};

QuestionAccordionHeader.propTypes = {
  disabled: PropTypes.bool,
  expanded: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  selectedLanguage: PropTypes.string.isRequired,
  onChangeQuestion: PropTypes.func.isRequired,
  question: PropTypes.array.isRequired,
  theme: PropTypes.string,
};

const QuestionAccordionBody = ({
  answers,
  correctAnswer,
  disabled,
  index,
  onChangeAnswers,
  onChangeSelected,
  selectedLanguage,
  theme: themeLayout,
}) => {
  const { t } = useTranslation();

  const handleChangeAnswers = (idxAnswer, answer) => {
    const newAnswers = answers.map((ans) => ({
      ...ans,
      answer: [...ans.answer],
    }));

    const currentAnswer = newAnswers[idxAnswer];

    const translationIndex = currentAnswer.answer.findIndex(
      (a) => a.languageCode === selectedLanguage
    );

    if (translationIndex !== -1) {
      if (answer) {
        currentAnswer.answer[translationIndex] = {
          ...currentAnswer.answer[translationIndex],
          value: answer,
        };
      } else {
        currentAnswer.answer.splice(translationIndex, 1);
      }
    } else if (answer) {
      currentAnswer.answer.push({
        languageCode: selectedLanguage,
        value: answer,
      });
    }

    onChangeAnswers(newAnswers);
  };

  const handleChangeSelected = (selected) => {
    const newAnswers = answers.map((ans) => ({
      ...ans,
      answer: [...ans.answer],
    }));

    newAnswers[correctAnswer - 1].correct = false;
    newAnswers[selected].correct = true;
    onChangeSelected(newAnswers);
  };

  const mainColor =
    themeLayout === 'light' ? theme.palette.mainDark : theme.palette.lightPure;

  return (
    <BodyStyled theme={themeLayout}>
      <BodyHeaderStyled>
        <T
          /* Fix width to align with radios inside AnswerRadio */
          sx={{ width: '72px' }}
          variant="helperText"
          fontWeight="semibold"
          color={mainColor}
        >
          {t('quizzes.selectCorrect')}
        </T>
        <T
          sx={{ alignSelf: 'end' }}
          variant="paragraphS"
          fontWeight="semibold"
          color={mainColor}
        >
          {t('quizzes.choiceAnswers')}
        </T>
      </BodyHeaderStyled>
      {answers.map(({ answer, correct }, indexOption) => (
        <AnswerRadio
          answer={answer}
          disabled={disabled}
          index={indexOption}
          key={`${index}-${indexOption}`}
          selectedLanguage={selectedLanguage}
          name={`question${index}`}
          onChangeAnswer={handleChangeAnswers}
          onChangeSelected={handleChangeSelected}
          selected={correct}
          theme={themeLayout}
        />
      ))}
    </BodyStyled>
  );
};

QuestionAccordionBody.propTypes = {
  answers: PropTypes.array.isRequired,
  correctAnswer: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  index: PropTypes.number.isRequired,
  selectedLanguage: PropTypes.string.isRequired,
  onChangeAnswers: PropTypes.func.isRequired,
  onChangeSelected: PropTypes.func.isRequired,
  theme: PropTypes.string,
};

const QuestionAccordion = ({
  disabled,
  index,
  isEditing,
  onChangeQuestion,
  openQuestion,
  question,
  selectedLanguage,
  setOpenQuestion,
  theme: themeLayout,
}) => {
  const expanded = openQuestion === index;
  const [internalExpanded, setInternalExpanded] = useState(
    isEditing ? expanded : true
  );

  const setExpanded = (expanded) => {
    if (!isEditing) {
      setInternalExpanded(expanded);
    } else if (expanded) {
      setOpenQuestion(index);
    } else {
      setOpenQuestion(-1);
    }
  };

  const handleQuestionChange = (text) => {
    const newQuestionTranslations = [...question.question];
    const translationIndex = newQuestionTranslations.findIndex(
      (q) => q.languageCode === selectedLanguage
    );

    if (translationIndex !== -1) {
      if (text) {
        newQuestionTranslations[translationIndex] = {
          ...newQuestionTranslations[translationIndex],
          value: text,
        };
      } else {
        newQuestionTranslations.splice(translationIndex, 1);
      }
    } else if (text) {
      newQuestionTranslations.push({
        languageCode: selectedLanguage,
        value: text,
      });
    }

    const newQuestion = {
      ...question,
      question: newQuestionTranslations,
    };
    onChangeQuestion(newQuestion);
  };

  const handleAnswerChange = (answers) => {
    const newQuestion = { ...question, answers };
    onChangeQuestion(newQuestion);
  };

  const handleSelectedChange = (answers) => {
    const newQuestion = { ...question, answers };
    onChangeQuestion(newQuestion);
  };

  const getCorrectAnswer = (answers) =>
    answers.findIndex(({ correct }) => correct) + 1;

  const { answers } = question;
  const sxAccordion = {
    marginBottom: theme.spacing.xs,
    boxShadow: 'none',
    backgroundColor: 'transparent',
  };

  const finalExpanded = !isEditing ? internalExpanded : expanded;

  return (
    <Accordion
      sx={sxAccordion}
      theme={themeLayout}
      expanded={finalExpanded}
      setExpanded={setExpanded}
      summary={
        <QuestionAccordionHeader
          disabled={disabled}
          expanded={finalExpanded}
          index={index}
          onChangeQuestion={handleQuestionChange}
          question={question.question}
          selectedLanguage={selectedLanguage}
          theme={themeLayout}
        />
      }
      details={
        <QuestionAccordionBody
          answers={answers}
          correctAnswer={getCorrectAnswer(answers)}
          disabled={disabled}
          index={index}
          onChangeAnswers={handleAnswerChange}
          onChangeSelected={handleSelectedChange}
          selectedLanguage={selectedLanguage}
          theme={themeLayout}
        />
      }
    />
  );
};

QuestionAccordion.propTypes = {
  disabled: PropTypes.bool,
  index: PropTypes.number.isRequired,
  isEditing: PropTypes.bool,
  onChangeQuestion: PropTypes.func.isRequired,
  openQuestion: PropTypes.number.isRequired,
  question: PropTypes.object.isRequired,
  selectedLanguage: PropTypes.string.isRequired,
  setOpenQuestion: PropTypes.func.isRequired,
  theme: PropTypes.string,
};

QuestionAccordion.defaultProps = {
  theme: 'light',
};

export default QuestionAccordion;
